import { faEye, faPen, faPlus, faTrash, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { useFormikContext } from "formik";
import { cloneDeep } from "lodash";
import React, { useState } from "react";
import { Button } from "react-bootstrap";

import { BPIRevenueMoreThan3CroresDetailsForm } from ".";
import { IBusinessIncomes, IFormITR1 } from "../../../config/interfaces";
import { useAppSelector } from "../../../redux/Store";
import { ITRFilingSelector } from "../../../redux/slice/ITRFilingSlice";
import FASvgIcon from "../../FASvgIcon";
import PreventEditComponent from "../../PreventEditComponent";
import PriceDisplay from "../../PriceDisplay";
import { ConfirmationModal } from "../../modals";

const BPIRevenueMoreThan3CroresDetails: React.FC = () => {
  const { isFilingReadOnly } = useAppSelector(ITRFilingSelector);

  const [showAddFormCanvas, setShowAddFormCanvas] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<IBusinessIncomes | null>(null);

  const { values, setFieldValue, setValues } = useFormikContext<IFormITR1>();

  const BusinessIncomesDetails = values.IncomeDetails?.BusinessIncomes;

  const handleDeleteItem = async () => {
    setShowDeleteModal(() => null);
    handleTotalCalculation(null);
  };

  const handleTotalCalculation = (businessIncomeData: IBusinessIncomes | null) => {
    const BISection44AD = Number(businessIncomeData?.Total || 0);

    const BPIncomes = {
      ...values.BusOrProfIncomes,
      BISection44AD: BISection44AD
    };

    const TotalBusOrProfIncome = Object.values(BPIncomes)?.reduce((acc, curr) => {
      return acc + curr;
    }, 0);

    const DATA = {
      BusOrProfIncomes: BPIncomes,
      TotalBusOrProfIncome: TotalBusOrProfIncome
    };

    setValues({ ...cloneDeep(values), ...DATA });
    setFieldValue("IncomeDetails.BusinessIncomes", businessIncomeData);
  };

  return (
    <>
      <section className="mb-5">
        <div className="section-title-wrap">
          <h5>{`Business Income (Revenue more than 3 crores) - Under section 44AD`}</h5>
          <PreventEditComponent>
            {BusinessIncomesDetails ? (
              <Button
                variant="warning"
                size="sm"
                className="text-white"
                onClick={() => setShowAddFormCanvas(() => true)}
              >
                <FASvgIcon icon={faPen} />
                Edit
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowAddFormCanvas(() => true)}
              >
                <FASvgIcon icon={faPlus} />
                Add
              </Button>
            )}
          </PreventEditComponent>

          {isFilingReadOnly && (
            <Button
              variant="warning"
              size="sm"
              className="text-white"
              onClick={() => setShowAddFormCanvas(() => true)}
            >
              <FASvgIcon icon={faEye} />
              View
            </Button>
          )}
        </div>

        {BusinessIncomesDetails ? (
          <dl className="eds-list eds-dl eds-inline-between">
            <RenderListItem
              title={"Business Income (Revenue more than 3 crores)"}
              sum={BusinessIncomesDetails.Total}
              onClickDeleteBtn={() => setShowDeleteModal(() => BusinessIncomesDetails)}
            />
          </dl>
        ) : null}
      </section>

      <BPIRevenueMoreThan3CroresDetailsForm
        showCanvas={showAddFormCanvas}
        handleCloseBtnPress={(businessIncomeData) => {
          if (businessIncomeData) {
            handleTotalCalculation(businessIncomeData);
          }
          setShowAddFormCanvas(() => false);
        }}
        formValues={BusinessIncomesDetails}
      />

      <ConfirmationModal
        showModal={!!showDeleteModal}
        iconColorClass="text-danger"
        title="Are you sure?"
        message={<p>{`You want to delete Business Income (Revenue more than 3 crores)`}</p>}
        onYesBtnClick={() => handleDeleteItem()}
        onNoBtnClick={() => setShowDeleteModal(() => null)}
        icon={faTrashAlt}
      />
    </>
  );
};

export default BPIRevenueMoreThan3CroresDetails;

const RenderListItem = ({
  title,
  sum = "0",
  onClickDeleteBtn
}: {
  title: string;
  sum: string;
  onClickDeleteBtn: () => void;
}) => {
  const shouldDisplay = sum !== "" && Number(sum) >= 0;

  return (
    <>
      {shouldDisplay ? (
        <div className="eds-list--item">
          <dt>{title}</dt>
          <dd>
            <span>
              <PriceDisplay price={sum} />
            </span>
            <PreventEditComponent>
              <Button
                variant="transparent"
                className="icon-only"
                onClick={() => onClickDeleteBtn()}
              >
                <FASvgIcon icon={faTrash} />
              </Button>
            </PreventEditComponent>
          </dd>
        </div>
      ) : null}
    </>
  );
};
